// NIFC aviation-frequency repeater map symbols.
//
// Two variants picked at draw time via the symbol layer's icon-image
// expression:
//   repeater-site    — actual repeater / receiver site (mountaintop).
//                       Vertical antenna mast with radiating wave arcs.
//   repeater-center  — dispatch-center pin (geocoded from ZIP, no
//                       coord in the PDF). Same antenna mast with a
//                       dashed halo to communicate 'approximate'.
//
// Designed to read at z>=6 without crowding airports / fixes / navaids.
// Emerald palette to distinguish from the amber obstacle symbols and
// the lime dispatch-zone polygons (which share the wildland-fire
// data family but mean different things).

import type { Map as MlMap } from 'maplibre-gl';
import { ensureSvgImages } from './iconRegistry';

const SIZE = 24;

interface Variant {
  id: string;
  /** True for the geocoded center-pin (approximate location). */
  approximate: boolean;
}

const VARIANTS: Variant[] = [
  { id: 'repeater-site',   approximate: false },
  { id: 'repeater-center', approximate: true  },
];

function svgFor(v: Variant): string {
  const cx = SIZE / 2;
  // Anchor: bottom of the mast (this is where the site/center lat/lon
  // actually points to). 'icon-anchor: bottom' on the symbol layer.
  const baseY = SIZE - 2;
  const tipY = 4;

  // Mast (vertical line, antenna pole).
  const mastColor = '#10b981';      // emerald-500
  const mastDark  = '#022c22';      // emerald-950 halo
  const mast = `
    <line x1="${cx}" y1="${baseY}" x2="${cx}" y2="${tipY}" stroke="${mastDark}" stroke-width="3.6" stroke-linecap="round"/>
    <line x1="${cx}" y1="${baseY}" x2="${cx}" y2="${tipY}" stroke="${mastColor}" stroke-width="1.8" stroke-linecap="round"/>
  `;

  // Top stub — small cap at the tip of the mast so it doesn't look
  // like an unfinished line.
  const cap = `<circle cx="${cx}" cy="${tipY}" r="1.2" fill="${mastColor}" stroke="${mastDark}" stroke-width="0.6"/>`;

  // Radio-wave arcs on both sides of the mast, emanating from the tip.
  // Two arcs (inner + outer) on each side — readable as "broadcasting"
  // at small icon sizes.
  const arcY = tipY + 0.4;
  const innerR = 4.2;
  const outerR = 7.0;
  const arcStroke = `stroke="${mastColor}" stroke-width="1.4" stroke-linecap="round" fill="none"`;
  const arcHalo   = `stroke="${mastDark}"  stroke-width="3.0" stroke-linecap="round" fill="none"`;
  // Each arc is a quarter-circle drawn with a Bezier; halo behind for
  // legibility against bright basemaps (sectional tan, sat imagery).
  const arcL = (r: number) =>
    `M ${cx - r},${arcY} A ${r} ${r} 0 0 1 ${cx - r * 0.5},${arcY - r * 0.85}`;
  const arcR = (r: number) =>
    `M ${cx + r},${arcY} A ${r} ${r} 0 0 0 ${cx + r * 0.5},${arcY - r * 0.85}`;
  const arcs = `
    <path d="${arcL(innerR)}" ${arcHalo}/>
    <path d="${arcR(innerR)}" ${arcHalo}/>
    <path d="${arcL(outerR)}" ${arcHalo}/>
    <path d="${arcR(outerR)}" ${arcHalo}/>
    <path d="${arcL(innerR)}" ${arcStroke}/>
    <path d="${arcR(innerR)}" ${arcStroke}/>
    <path d="${arcL(outerR)}" ${arcStroke}/>
    <path d="${arcR(outerR)}" ${arcStroke}/>
  `;

  // Ground line — short horizontal at the base.
  const ground = `<rect x="${cx - 3}" y="${baseY - 0.2}" width="6" height="1.6" fill="${mastDark}" rx="0.4"/>`;

  // Approximate-location ring: a dashed emerald circle around the base
  // for the center-pin variant. Communicates "this dispatch center
  // is at city/ZIP centroid, not a real repeater coord".
  const approxRing = v.approximate
    ? `<circle cx="${cx}" cy="${baseY - 0.5}" r="6.5" fill="none" stroke="${mastColor}" stroke-width="1.2" stroke-dasharray="2 1.6" opacity="0.85"/>`
    : '';

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
      ${approxRing}
      ${arcs}
      ${mast}
      ${cap}
      ${ground}
    </svg>
  `.trim();
}

const SYMBOLS = VARIANTS.map((v) => ({ id: v.id, svg: svgFor(v) }));

/** Style-expression that picks the right icon per feature.
 *  Reads `feature_kind` from the GeoJSON properties — 'center' for
 *  geocoded dispatch-center pins, anything else (including 'site' or
 *  unset) gets the standard repeater glyph. */
export const REPEATER_ICON_EXPRESSION: any = [
  'case',
  ['==', ['get', 'feature_kind'], 'center'],
  'repeater-center',
  'repeater-site',
];

export function ensureRepeaterIcons(map: MlMap): Promise<void> {
  return ensureSvgImages(map, SYMBOLS, {
    defaultSize: SIZE,
    logTag: '[repeaterIcons]',
    pixelRatioCap: 2,
  });
}
